import React, { useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, CheckCircle, AlertTriangle, Wrench, CalendarCheck } from 'lucide-react';
import JobCard from './JobCard';

const ApplicatorWorkQueue = ({ jobs, user, can, jobCallbacks }) => {
  const myId = user?.id || user?.staffId;

  const myJobs = useMemo(() =>
    jobs.filter(j => j.technicianId === myId && !['teslim_edildi', 'completed'].includes(j.status)),
    [jobs, myId]
  );

  const todayStr = new Date().toISOString().split('T')[0];

  const todayJobs = useMemo(() =>
    myJobs.filter(j => j.arrivalDate?.startsWith(todayStr) || j.installationDate?.startsWith(todayStr) || j.createdAt?.startsWith(todayStr)),
    [myJobs, todayStr]
  );

  const inProgressJobs = useMemo(() =>
    myJobs.filter(j => ['islemde', 'in_progress', 'kalite_kontrol'].includes(j.status)),
    [myJobs]
  );

  const waitingJobs = useMemo(() =>
    myJobs.filter(j => ['arac_kabul', 'beklemede', 'pending', 'randevu_planlandi'].includes(j.status)),
    [myJobs]
  );

  const readyJobs = useMemo(() =>
    myJobs.filter(j => ['teslime_hazir', 'ready'].includes(j.status)),
    [myJobs]
  );

  const delayedJobs = useMemo(() => myJobs.filter(j => j.isDelayed), [myJobs]);

  // Tüm işler (highlight ile)
  const allActiveCount = jobs.filter(j => !['teslim_edildi', 'completed'].includes(j.status)).length;

  return (
    <div className="space-y-4" data-testid="applicator-work-queue">
      {/* Kısa özet kartları */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Card className="bg-brand/20 border-brand/40">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-brand">{myJobs.length}</p>
            <p className="text-xs text-gray-400">Bana Atanan</p>
          </CardContent>
        </Card>
        <Card className="bg-cyan-900/20 border-cyan-700/40">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-cyan-400">{todayJobs.length}</p>
            <p className="text-xs text-gray-400">Bugün</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-900/20 border-blue-700/40">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-400">{inProgressJobs.length}</p>
            <p className="text-xs text-gray-400">İşlemde</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-900/20 border-yellow-700/40">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-yellow-400">{waitingJobs.length}</p>
            <p className="text-xs text-gray-400">Bekleyen</p>
          </CardContent>
        </Card>
        <Card className="bg-green-900/20 border-green-700/40">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-400">{readyJobs.length}</p>
            <p className="text-xs text-gray-400">Teslime Hazır</p>
          </CardContent>
        </Card>
      </div>

      {/* Geciken İşler */}
      {delayedJobs.length > 0 && (
        <div className="border border-red-700/40 rounded-lg bg-red-900/10 p-3">
          <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4" />Geciken İşlerim ({delayedJobs.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {delayedJobs.map(j => <JobCard key={j.id} job={j} can={can} {...jobCallbacks} />)}
          </div>
        </div>
      )}

      {/* Bugün Yapacaklarım */}
      {todayJobs.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-cyan-400 flex items-center gap-2 mb-2">
            <CalendarCheck className="h-4 w-4" />Bugün Yapacaklarım ({todayJobs.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {todayJobs.map(j => <JobCard key={j.id} job={j} can={can} {...jobCallbacks} />)}
          </div>
        </div>
      )}

      {/* İşlemde Olanlar */}
      {inProgressJobs.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-blue-400 flex items-center gap-2 mb-2">
            <Wrench className="h-4 w-4" />İşlemde ({inProgressJobs.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {inProgressJobs.map(j => <JobCard key={j.id} job={j} can={can} {...jobCallbacks} />)}
          </div>
        </div>
      )}

      {/* Bekleyen İşler */}
      {waitingJobs.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-yellow-400 flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4" />Bekleyen İşler ({waitingJobs.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {waitingJobs.map(j => <JobCard key={j.id} job={j} can={can} {...jobCallbacks} />)}
          </div>
        </div>
      )}

      {/* Teslime Hazır */}
      {readyJobs.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-green-400 flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4" />Teslime Hazır ({readyJobs.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {readyJobs.map(j => <JobCard key={j.id} job={j} can={can} {...jobCallbacks} />)}
          </div>
        </div>
      )}

      {myJobs.length === 0 && (
        <div className="text-center text-gray-400 py-8">
          <Wrench className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Atanmış aktif iş bulunmuyor</p>
        </div>
      )}

      <p className="text-gray-600 text-xs text-right">Toplam aktif iş: {allActiveCount} | Bana atanan: {myJobs.length}</p>
    </div>
  );
};

export default ApplicatorWorkQueue;
